import {Placement} from '@popperjs/core';
import {first} from 'lodash';
import React, {FC, RefObject} from 'react';
import styled from 'styled-components';

import {buildInteractionIdProps} from '../../../../libs/shared/core/src/helpers/interaction/interactionHelpers';
import {Trans} from '../../../../libs/shared/core/src/helpers/internationalization/react';
import {useConstant} from '../../../../libs/shared/core/src/helpers/react/hooks/hookHelpers';
import {AsyncStatusesEnum} from '../../../../libs/shared/core/src/helpers/types/asyncStatuses';
import {ConversationSummaryStatesEnum} from '../../../../libs/shared/core/src/models/api/conversationCommonTypes';
import {ConversationInquiryModel} from '../../../../libs/shared/core/src/models/api/conversationInquiriesModel';
import {ConversationSummaryFactModel} from '../../../../libs/shared/core/src/models/api/factModel';
import {
  LoadedMessageModel,
  MessageVisualTypesEnum,
} from '../../../../libs/shared/core/src/models/api/messageModel';
import {NewPanelId} from '../../../../libs/shared/core/src/store/panels/panelStateBase';
import {TimelineVariationsEnum} from '../../../../libs/shared/core/src/store/timelineVariations/timelineVariationsTypes';
import {IconButton} from '../../../../libs/shared/ui/src/components/buttons/iconButton';
import {Icon} from '../../../../libs/shared/ui/src/components/icon/icon';
import {AsyncLoader} from '../../../../libs/shared/ui/src/components/loaders/asyncLoader';
import {PreviewReposition} from '../../../../libs/shared/ui/src/components/reposition/previewReposition';
import {shadows} from '../../../../libs/shared/ui/src/styles/colorStyles';
import {VisualSizesEnum} from '../../../../libs/shared/ui/src/styles/commonStyles';
import {fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {isInternalConversation} from '../../../helpers/models/conversationHelpers';
import {VisualRecipientMap} from '../../../helpers/models/recipientHelpers';
import {ConversationModel} from '../../../models/api/conversationModel';
import {openMessageRenderPopup} from '../../../services/popups';
import {MessageViewerChatBody} from '../../inboxes/viewer/activity/message/chat/messageViewerChatBody';
import {MessageViewerEmailBody} from '../../inboxes/viewer/activity/message/email/messageViewerEmailBody';
import {MessageViewerAvatar} from '../../inboxes/viewer/activity/message/messageViewerAvatar';
import {MessageViewerDetails} from '../../inboxes/viewer/activity/message/messageViewerDetails';
import {MessageViewerTweetBody} from '../../inboxes/viewer/activity/message/tweet/messageViewerTweetBody';
import {ConversationSummaryAiSummarize} from '../../inboxes/viewer/conversation/conversationSummary/conversationSummaryAiSummarize';
import {InquiryStatusIcon} from '../../inboxes/viewer/conversation/synopsis/synopsisInquiryIcon';
import {ConversationPreviewSkeleton} from './skeleton/conversationPreviewSkeleton';

/*
 * Types.
 */

export interface ConversationPreviewProps {
  panelId: NewPanelId;
  conversationId: number;
  anchor: RefObject<HTMLElement>;
  viewerConversationId: number | undefined;
  message: LoadedMessageModel | undefined;
  conversation: ConversationModel;
  recipients: VisualRecipientMap;
  similarInquiry?: ConversationInquiryModel;
  asyncStatus: AsyncStatusesEnum;
  summary?: ConversationSummaryFactModel;
  shouldRenderSummaryIfAvailable?: boolean;
  onRetryClick: () => void;
  hasAiSummarizeFeature: boolean;
  placement?: Placement;
  openConversation?: (model: ConversationModel) => void;
}

/*
 * Style.
 */

const StyledPreviewDiv = styled.div`
  display: flex;
  flex-flow: column;
  overflow: hidden;
  border-radius: 16px;
  background-color: ${(p) => p.theme.foreground};
  ${shadows.dialogsAndDropdowns};

  width: 360px;
  max-height: 350px;
`;

const StyledCurrentlyViewingDiv = styled.div`
  border-radius: 20px;
  background-color: ${(p) => p.theme.foreground};
  ${shadows.dialogsAndDropdowns};
  padding: 8px 10px;
  margin: 10px;
  color: ${(p) => p.theme.greys.shade80};
  font-style: italic;
`;

const StyledDefaultPaddingDiv = styled.div`
  padding: 16px;
`;

const StyledHeaderDiv = styled.div`
  display: grid;
  grid-template-areas: 'avatar details';
  grid-template-columns: min-content 1fr;
  padding: 10px 9px 0 9px;
`;

const StyledHeaderDetailsDiv = styled.div`
  grid-area: details;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const StyledHeaderWrapperDiv = styled.div`
  display: flex;
  flex-flow: row;
  padding: 0px 16px;
  align-items: center;
  gap: 5px;
  border-bottom: 1px solid ${(p) => p.theme.alphas.black20};
`;

const StyledHeaderTextDiv = styled.div`
  flex: 1;
  line-height: 18px;
  font-weight: ${fontWeights.semibold};
  padding: 14px 0;
  color: ${(p) => p.theme.text};
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const StyledContentWrapperDiv = styled.div`
  overflow: auto;
`;

const StyledSummaryWrapperDiv = styled.div`
  display: flex;
  flex-flow: column;
  padding: 16px;
  color: ${(p) => p.theme.text};

  // Enables selecting the content found within the summary. This goes
  // deep into the children since they are nested lists.
  & * {
    -webkit-user-select: text;
    user-select: text;
  }
`;

const StyledInquiryWrapperDiv = styled.div`
  display: flex;
  flex-direction: row;
  padding: 16px;
  color: ${(p) => p.theme.text};
  gap: 10px;
`;

const StyledInquiryStatusIcon = styled(InquiryStatusIcon)`
  margin-top: 3px;
`;

const StyledInquiryBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  line-height: 1.4;
`;

const StyledSummaryTitleDiv = styled.div`
  font-weight: ${fontWeights.medium};
  line-height: 20px;
  color: ${(p) => p.theme.text};
`;

enum ConversationPreviewTypesEnum {
  SUMMARY = 'summary',
  LAST_ACTIVITY = 'last_activity',
  SIMILAR_INQUIRY = 'similar_inquiry',
}

/*
 * Component.
 */

export const ConversationPreview: FC<ConversationPreviewProps> = (props) => {
  const {conversation, message, similarInquiry, hasAiSummarizeFeature, placement} = props;
  const popperPlacement = placement ?? 'left';

  const isLoadingSummary = conversation.summaryState === ConversationSummaryStatesEnum.LOADING;

  // Prefer summary if available and feature is enabled.
  const displayType = useConstant(() => {
    if (similarInquiry) {
      return ConversationPreviewTypesEnum.SIMILAR_INQUIRY;
    }

    return props.shouldRenderSummaryIfAvailable &&
      (props.summary || isLoadingSummary) &&
      hasAiSummarizeFeature
      ? ConversationPreviewTypesEnum.SUMMARY
      : ConversationPreviewTypesEnum.LAST_ACTIVITY;
  });

  // Do not render any previewer for discussions.
  const isDiscussion = isInternalConversation(conversation);
  if (isDiscussion) {
    return null;
  }

  // If there is no message but a draft seed, someone is working on it.
  // do not display preview. draftSeeds is only drafts of other teammates,
  // so we need to check draftSeeds and lastDraft.
  const {draftSeeds} = conversation;
  const teammateHasDraft = Boolean(draftSeeds?.length > 0);
  const userHasDraft = Boolean(conversation.lastDraft);
  const hasDraft = teammateHasDraft || userHasDraft;
  if (!message && hasDraft) {
    return null;
  }

  const {conversationId, onRetryClick, viewerConversationId} = props;
  if (conversationId === viewerConversationId) {
    return (
      <PreviewReposition anchor={props.anchor} popperPlacement={popperPlacement} shouldHideArrow>
        <StyledCurrentlyViewingDiv>
          <Trans id="conversation-preview-currently-viewing">Currently viewing</Trans>
        </StyledCurrentlyViewingDiv>
      </PreviewReposition>
    );
  }

  return (
    <PreviewReposition anchor={props.anchor} popperPlacement={popperPlacement}>
      <StyledPreviewDiv {...buildInteractionIdProps(`conversation-preview-${conversationId}`)}>
        {renderContent(props, onRetryClick, displayType)}
      </StyledPreviewDiv>
    </PreviewReposition>
  );
};

function renderContent(
  props: ConversationPreviewProps,
  onRetryClick: () => void,
  displayType: ConversationPreviewTypesEnum,
) {
  const {message, asyncStatus, conversation, similarInquiry} = props;

  if (similarInquiry) {
    return (
      <StyledContentWrapperDiv>{renderSimilarInquiry(similarInquiry, displayType)}</StyledContentWrapperDiv>
    );
  }

  // If the "Last message" is loading.
  if (asyncStatus === AsyncStatusesEnum.LOADING) {
    return <ConversationPreviewSkeleton />;
  }

  // If there is a summary being generated.
  if (conversation.summaryState === ConversationSummaryStatesEnum.LOADING) {
    return <ConversationPreviewSkeleton />;
  }

  if (asyncStatus === AsyncStatusesEnum.FAILED || !message) {
    return (
      <StyledDefaultPaddingDiv>
        <AsyncLoader
          size={VisualSizesEnum.LARGE}
          status={AsyncStatusesEnum.FAILED}
          error={<Trans id="conversation-preview-error">Something went wrong.</Trans>}
          onRetryClick={onRetryClick}
        />
      </StyledDefaultPaddingDiv>
    );
  }

  return (
    <>
      {renderHeaderBar(displayType, props)}
      <StyledContentWrapperDiv>
        {maybeRenderHeader(message, props, displayType)}
        {maybeRenderMessageBody(message, props, displayType)}
      </StyledContentWrapperDiv>
    </>
  );
}

function renderHeaderBar(displayType: ConversationPreviewTypesEnum, props: ConversationPreviewProps) {
  switch (displayType) {
    case ConversationPreviewTypesEnum.SUMMARY:
      return (
        <StyledHeaderWrapperDiv>
          <Icon name="sparklesAiFilled" />
          <StyledHeaderTextDiv>
            <Trans id="conversation-preview-summary-header">Summary</Trans>
            {maybeRenderOpenConversationButton(props)}
          </StyledHeaderTextDiv>
        </StyledHeaderWrapperDiv>
      );

    case ConversationPreviewTypesEnum.SIMILAR_INQUIRY:
      return (
        <StyledHeaderWrapperDiv>
          <Icon name="sparklesAiFilled" />
          <StyledHeaderTextDiv>
            <Trans id="conversation-preview-inquiry-header">Similar request</Trans>
          </StyledHeaderTextDiv>
        </StyledHeaderWrapperDiv>
      );

    case ConversationPreviewTypesEnum.LAST_ACTIVITY:
    default:
      return (
        <StyledHeaderWrapperDiv>
          <StyledHeaderTextDiv>
            <Trans id="conversation-preview-latest-message-header">Latest message</Trans>
            {maybeRenderOpenConversationButton(props)}
          </StyledHeaderTextDiv>
        </StyledHeaderWrapperDiv>
      );
  }
}
function maybeRenderOpenConversationButton(props: ConversationPreviewProps) {
  const {openConversation, conversation} = props;

  if (!openConversation) {
    return null;
  }

  return (
    <IconButton
      iconName="popoutSmall"
      onClick={() => openConversation(conversation)}
      label={<Trans id="conversation-header-view-in-new-window">View in new window</Trans>}
      iconSize={16}
    />
  );
}

function maybeRenderHeader(
  message: LoadedMessageModel,
  props: ConversationPreviewProps,
  displayType: ConversationPreviewTypesEnum,
) {
  const {recipients, panelId} = props;

  if (displayType !== ConversationPreviewTypesEnum.LAST_ACTIVITY) {
    return null;
  }

  return (
    <StyledHeaderDiv>
      <MessageViewerAvatar panelId={panelId} message={message} author={first(recipients.from)} />
      <StyledHeaderDetailsDiv>
        <MessageViewerDetails
          panelId={panelId}
          message={message}
          visualRecipients={recipients}
          activitySourceVisualType={undefined}
          isMessageViewerExpanded
          shouldShowSubjectWhenCollapsed
          hasMessageAnnotations={false}
        />
      </StyledHeaderDetailsDiv>
    </StyledHeaderDiv>
  );
}

function maybeRenderMessageBody(
  message: LoadedMessageModel,
  props: ConversationPreviewProps,
  displayType: ConversationPreviewTypesEnum,
) {
  const {panelId, summary, similarInquiry} = props;

  if (summary && displayType === ConversationPreviewTypesEnum.SUMMARY) {
    return (
      <StyledSummaryWrapperDiv>
        <StyledSummaryTitleDiv>{summary.oneliner}</StyledSummaryTitleDiv>
        <ConversationSummaryAiSummarize summary={summary} size={VisualSizesEnum.MEDIUM} />
      </StyledSummaryWrapperDiv>
    );
  }

  if (similarInquiry && displayType === ConversationPreviewTypesEnum.SIMILAR_INQUIRY) {
    return renderSimilarInquiry(similarInquiry, displayType);
  }

  switch (message.visualType) {
    case MessageVisualTypesEnum.EMAIL:
      return (
        <MessageViewerEmailBody
          message={message}
          panelId={panelId}
          hasMessageAnnotations={false}
          isExternalContentHidden={false}
          onOpenMessageRenderClick={() => openMessageRenderPopup(message)}
        />
      );
    case MessageVisualTypesEnum.TWEET:
      return (
        <MessageViewerTweetBody
          message={message}
          panelId={panelId}
          hasMessageAnnotations={false}
          timelineVariation={TimelineVariationsEnum.ASYNCHRONOUS}
        />
      );
    case MessageVisualTypesEnum.CHAT:
      return (
        <MessageViewerChatBody
          message={message}
          panelId={panelId}
          hasMessageAnnotations={false}
          timelineVariation={TimelineVariationsEnum.ASYNCHRONOUS}
          qualBotResponses={[]}
        />
      );
    default:
      return null;
  }
}

function renderSimilarInquiry(
  similarInquiry: ConversationInquiryModel,
  displayType: ConversationPreviewTypesEnum,
) {
  const {resolution, takeaways} = similarInquiry;

  if (displayType !== ConversationPreviewTypesEnum.SIMILAR_INQUIRY) {
    return null;
  }

  const summary = resolution?.summary ?? takeaways[0]?.summary;

  return (
    <StyledInquiryWrapperDiv>
      <div>
        <StyledInquiryStatusIcon inquiry={similarInquiry} />
      </div>
      <StyledInquiryBody>
        <div>
          <strong>
            {similarInquiry.summary.subject} {similarInquiry.summary.verb} {similarInquiry.summary.remainder}
          </strong>
        </div>
        {summary && (
          <div>
            {summary.subject} {summary.verb} {summary.remainder}
          </div>
        )}
      </StyledInquiryBody>
    </StyledInquiryWrapperDiv>
  );
}
